import log from 'loglevel'
import { TileLayer } from '@deck.gl/geo-layers'
import { BitmapLayer } from '@deck.gl/layers'
import { parse } from '@loaders.gl/core'
import { RestApiClient } from 'ifield-core-components'

// レイヤーID
const LAYER_ID = 'iFieldRasterTile'
// サブレイヤーID
const SUB_LAYER_ID = Object.freeze({
  INDOOR_LEVEL: 'indoorLevel',
  MAP_IMAGE: 'mapImage',
})
// サブレイヤーIDとQueryPatameterの対応Map
const SUB_LAYER_ID_TO_PARAMETER = new Map([
  [SUB_LAYER_ID.INDOOR_LEVEL, 'indoor'],
  [SUB_LAYER_ID.MAP_IMAGE, 'map_image'],
])

/**
 * iFieldが配信するラスタータイルのレンダラー
 */
class IFieldRasterTileRenderer {
  /**
   * タイルURL取得
   * @param {Object} style 親タイルレイヤーのスタイル
   * @param {Array.<Object>} subLayers サブレイヤーのパラメータ
   */
  _getTileUrl(style, subLayers) {
    const indoorLevelId = this._getIndoorLevelId(style)
    const layersParam = this._getLayersParam(subLayers)

    const base = `${process.env.VUE_APP_API_BASE_PATH}`
      .replace('users/api', 'tile')
      .replace('admin/api', 'tile')
    const subPath = indoorLevelId ? `indoor_level/${indoorLevelId}` : 'global'
    const url = RestApiClient.createApiUrl(`${subPath}/{z}/{x}/{y}.png`, base)
    if (!indoorLevelId && layersParam) {
      url.searchParams.append('layers', layersParam)
    }
    log.debug(`[DEBUG] tileLayer url(${url.toString()})`)
    return decodeURI(url)
  }

  /**
   * フロアIDを取得する
   * @param {Object} style タイルレイヤーのパラメータ
   * @returns {Number} フロアID
   */
  _getIndoorLevelId(style) {
    return (style && style.indoorLevelId) || null
  }

  /**
   * クエリパラメータに設定する文字列を取得する
   * @param {Array.<Object>} subLayers サブレイヤーのパラメータ
   * @returns {String} layers クエリパラメータに設定する文字列(無い場合は null)
   */
  _getLayersParam(subLayers) {
    const params = []
    if (subLayers) {
      for (const layer of subLayers) {
        if (!layer.visible) continue
        const paramString = SUB_LAYER_ID_TO_PARAMETER.get(layer.id)
        paramString && params.push(paramString)
      }
    }

    return params.length ? params.join(',') : null
  }

  /**
   * レイヤーのvisibleを取得する
   * @param {Boolean} visible 親レイヤーのvisible
   * @param {Array.<Object>} subLayers サブレイヤーのパラメータ
   * @returns {Boolean}
   */
  _getVisible({ visible, subLayers }) {
    return visible && (subLayers ? subLayers.some((l) => l.visible) : true)
  }

  /**
   * レンダラー関数を作成する
   * @param {Object} param 描画パラメータ
   * @returns {Function.<TileLayer>} レンダラー
   */
  createRenderer(param) {
    log.debug('[DEBUG] iFieldRasterTileRenderer create', param)

    const tileUrl = this._getTileUrl(param.style, param.subLayers)
    const visible = this._getVisible(param)
    const { onTileError, onFetchError } = param.callback || {}

    return {
      id: LAYER_ID,
      render: (zoom) =>
        new TileLayer({
          id: LAYER_ID,
          data: tileUrl,
          visible,
          tileSize: 256,
          pickable: false,
          fetch: async (url, { layer, loaders, loadOptions, signal }) => {
            const tileLoaders = loaders || layer.props.loaders
            const response = await fetch(url, {
              signal,
              method: 'GET',
              mode: 'cors',
              credentials: 'include',
            })
            if (!response.ok) {
              onFetchError && onFetchError(response.clone())
            }
            return parse(response, tileLoaders, loadOptions)
          },
          parameters: {
            depthTest: false,
            blend: true,
          },
          renderSubLayers(props) {
            const {
              bbox: { west, south, east, north },
            } = props.tile

            return new BitmapLayer(props, {
              data: null,
              image: props.data,
              bounds: [west, south, east, north],
            })
          },
          onTileError: (error) => {
            onTileError ? onTileError(error) : log.error(error, zoom)
          },
        }),
    }
  }
}

IFieldRasterTileRenderer.LAYER_ID = LAYER_ID
IFieldRasterTileRenderer.SUB_LAYER_ID = SUB_LAYER_ID

export default IFieldRasterTileRenderer
